import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, Heart, Share2, Flag, Plus, Search,
  Image as ImageIcon, Video, Link2, Send, X, MoreHorizontal,
  Loader2, AlertCircle, CheckCircle, Trash2, Users, Smile,
  MessageCircle, Radio, MapPin
} from 'lucide-react';
import api from '../utils/api';
import Header from '../components/Header';
import { isHeicFile, convertHeicToJpeg, ACCEPTED_IMAGE_FORMATS } from '../utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 172800) return 'Yesterday';
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  // For older posts, show the full date
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Hook to get current user
const useCurrentUser = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/auth/me');
        // The API returns user data directly, extract first_name from name if needed
        const userData = res.data;
        if (userData && !userData.first_name && userData.name) {
          const nameParts = userData.name.split(' ');
          userData.first_name = nameParts[0] || '';
          userData.last_name = nameParts.slice(1).join(' ') || '';
        }
        setUser(userData);
      } catch (err) {
        console.error('Auth check failed:', err);
        // Token might be expired, clear it
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
        }
        setUser(null);
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  return { user, loading };
};

const POST_TYPES = [
  { id: 'all', label: 'All', icon: '📋', color: 'gray' },
  { id: 'general', label: 'General', icon: '💬', color: 'blue' },
  { id: 'deal_alert', label: 'Deal Alerts', icon: '🔥', color: 'red' },
  { id: 'question', label: 'Questions', icon: '❓', color: 'purple' },
  { id: 'showcase', label: 'Showcase', icon: '🏆', color: 'yellow' },
  { id: 'news', label: 'News', icon: '📰', color: 'green' }
];

const REPORT_REASONS = [
  { id: 'spam', label: 'Spam' },
  { id: 'scam', label: 'Scam or fraud' },
  { id: 'harassment', label: 'Harassment or bullying' },
  { id: 'adult_content', label: 'Adult content' },
  { id: 'hate', label: 'Hate speech' },
  { id: 'illegal_item', label: 'Illegal item' },
  { id: 'other', label: 'Other' }
];

// ============ ENHANCED POST COMPOSER ============
const PostComposer = ({ user, onPost, userListings = [] }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [type, setType] = useState('general');
  const [textBody, setTextBody] = useState('');
  const [mediaUrls, setMediaUrls] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [listingId, setListingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const textareaRef = useRef(null);

  // Get user's location on mount
  useEffect(() => {
    const getLocation = async () => {
      setLocationLoading(true);
      try {
        // Try to get location from IP-based geolocation (more reliable for city)
        const res = await fetch('https://ipapi.co/json/');
        if (res.ok) {
          const data = await res.json();
          if (data.city && data.country_name) {
            setLocation(`${data.city}, ${data.country_name}`);
          } else if (data.country_name) {
            setLocation(data.country_name);
          }
        }
      } catch (e) {
        console.log('Location detection failed:', e);
      } finally {
        setLocationLoading(false);
      }
    };
    getLocation();
  }, []);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (mediaUrls.length + files.length > 10) {
      setError('Maximum 10 images allowed');
      return;
    }

    setUploading(true);
    for (const file of files) {
      try {
        // Convert HEIC to JPEG if needed
        let processedFile = file;
        if (isHeicFile(file)) {
          try {
            processedFile = await convertHeicToJpeg(file);
          } catch (convError) {
            console.error('HEIC conversion error:', convError);
            setError('Failed to convert HEIC image. Please try a different format.');
            continue;
          }
        }
        
        const formData = new FormData();
        formData.append('file', processedFile);
        const res = await api.post('/upload', formData);
        setMediaUrls(prev => [...prev, res.data.url]);
      } catch (e) {
        console.error('Upload error:', e);
      }
    }
    setUploading(false);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload', formData);
      setVideoUrl(res.data.url);
    } catch (e) {
      setError('Failed to upload video');
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!textBody.trim()) {
      setError('Please write something');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          type,
          text_body: textBody,
          media_urls: mediaUrls,
          video_url: videoUrl || null,
          listing_id: listingId || null,
          location: location || null
        })
      });

      const data = await res.json();

      if (res.ok) {
        onPost?.(data.post);
        setTextBody('');
        setMediaUrls([]);
        setVideoUrl('');
        setListingId('');
        setType('general');
        setIsExpanded(false);
      } else {
        // Show specific error from server
        const errorMsg = data.detail || data.message || 'Failed to create post';
        setError(errorMsg);
        console.error('Post creation error:', data);
      }
    } catch (e) {
      console.error('Post creation exception:', e);
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  if (!user) {
    return (
      <div 
        onClick={() => navigate('/auth')}
        className="bg-white rounded-xl shadow-sm p-4 mb-6 cursor-pointer hover:shadow-md transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
            <Users className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-gray-400">
            Sign in to share with the community...
          </div>
        </div>
      </div>
    );
  }

  // Show email verification message if not verified
  if (!user.email_verified) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex items-center gap-3 text-amber-600">
          <AlertCircle className="h-5 w-5" />
          <div>
            <p className="font-medium">Email verification required</p>
            <p className="text-sm text-gray-500">Please verify your email to post in the community. Check your inbox for the verification link.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
      {/* Composer Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
            {user.profile_image ? (
              <img src={user.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              user.first_name?.charAt(0)?.toUpperCase() || 'U'
            )}
          </div>
          <div className="flex-1">
            {isExpanded ? (
              <textarea
                ref={textareaRef}
                value={textBody}
                onChange={(e) => setTextBody(e.target.value)}
                placeholder={`What's on your mind, ${user.first_name || 'friend'}?`}
                className="w-full p-3 bg-gray-50 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[100px]"
                autoFocus
                data-testid="post-composer-input"
              />
            ) : (
              <div 
                onClick={() => setIsExpanded(true)}
                className="w-full bg-gray-100 hover:bg-gray-200 rounded-full px-4 py-2.5 text-gray-500 cursor-pointer transition"
              >
                What's on your mind, {user.first_name || 'friend'}?
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <>
            {/* Post Type Selection */}
            <div className="mt-4 flex flex-wrap gap-2">
              {POST_TYPES.filter(t => t.id !== 'all').map((t) => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                    type === t.id 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Media Previews */}
            {(mediaUrls.length > 0 || videoUrl) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {mediaUrls.map((url, idx) => (
                  <div key={idx} className="relative">
                    <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg" />
                    <button 
                      onClick={() => setMediaUrls(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {videoUrl && (
                  <div className="relative">
                    <video src={videoUrl} className="w-32 h-20 object-cover rounded-lg" />
                    <button 
                      onClick={() => setVideoUrl('')}
                      className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Listing Attachment */}
            {userListings.length > 0 && listingId && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                <Link2 className="h-5 w-5 text-emerald-500" />
                <span className="text-sm text-gray-600">
                  {userListings.find(l => l.listing_id === listingId)?.title || 'Listing attached'}
                </span>
                <button onClick={() => setListingId('')} className="ml-auto">
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            )}
          </>
        )}
      </div>

      {/* Action Bar */}
      {isExpanded && (
        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-200 cursor-pointer transition">
              <ImageIcon className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium text-gray-600 hidden sm:inline">Photo</span>
              <input 
                type="file" 
                accept={ACCEPTED_IMAGE_FORMATS} 
                multiple 
                className="hidden" 
                onChange={handleImageUpload}
                disabled={uploading}
              />
            </label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-200 cursor-pointer transition">
              <Video className="h-5 w-5 text-red-500" />
              <span className="text-sm font-medium text-gray-600 hidden sm:inline">Video</span>
              <input 
                type="file" 
                accept="video/*" 
                className="hidden" 
                onChange={handleVideoUpload}
                disabled={uploading || videoUrl}
              />
            </label>
            {userListings.length > 0 && (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-200 transition">
                  <Link2 className="h-5 w-5 text-blue-500" />
                  <span className="text-sm font-medium text-gray-600 hidden sm:inline">Listing</span>
                </button>
                <div className="absolute left-0 top-full mt-1 bg-white shadow-lg rounded-lg border py-1 z-10 min-w-[200px] hidden group-hover:block">
                  {userListings.slice(0, 5).map((listing) => (
                    <button
                      key={listing.listing_id}
                      onClick={() => setListingId(listing.listing_id)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 truncate"
                    >
                      {listing.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Location indicator */}
            {location && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <MapPin className="h-3 w-3" />
                <span>{location}</span>
              </div>
            )}
            {locationLoading && (
              <div className="flex items-center gap-1 text-xs text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Getting location...</span>
              </div>
            )}
            
            {uploading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            <button
              onClick={() => { setIsExpanded(false); setTextBody(''); setMediaUrls([]); setVideoUrl(''); }}
              className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !textBody.trim()}
              className="px-5 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="post-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ COMMUNITY CHAT ROOM ============
const REACTION_EMOJIS = ['👍', '❤️', '😂', '🔥', '😮', '😢'];

const ChatRoom = ({ user }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineCount, setOnlineCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [activeReactionPicker, setActiveReactionPicker] = useState(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    // Fetch chat history first
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/community/chat/history`);
        const data = await res.json();
        setMessages(data.messages || []);
        setOnlineCount(data.online_count || 0);
      } catch (e) {
        console.error('Error fetching chat history:', e);
      }
    };
    fetchHistory();

    // Connect WebSocket
    const token = localStorage.getItem('token');
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/community/chat/ws${token ? `?token=${token}` : ''}`);

    ws.onopen = () => {
      setConnected(true);
      // Ping to keep alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      if (event.data === 'pong') return;

      try {
        const data = JSON.parse(event.data);

        if (data.type === 'message') {
          setMessages(prev => [...prev.slice(-99), data]); // Keep last 100 messages
        } else if (data.type === 'user_joined' || data.type === 'user_left') {
          setOnlineCount(data.online_count);
        } else if (data.type === 'reaction_update') {
          // Update reactions on a message
          setMessages(prev => prev.map(msg => 
            msg.message_id === data.message_id 
              ? { ...msg, reactions: data.reactions }
              : msg
          ));
        } else if (data.type === 'error') {
          setError(data.message);
          setTimeout(() => setError(''), 3000);
        }
      } catch (e) {}
    };

    ws.onerror = () => setConnected(false);
    ws.onclose = () => {
      setConnected(false);
      if (ws.pingInterval) clearInterval(ws.pingInterval);
    };

    wsRef.current = ws;

    return () => {
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      ws.close();
    };
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveReactionPicker(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSend = () => {
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    if (!user) {
      setError('Sign in to send messages');
      setTimeout(() => setError(''), 3000);
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: newMessage.trim()
    }));

    setNewMessage('');
    inputRef.current?.focus();
  };

  const handleReaction = (messageId, emoji) => {
    if (!user) {
      setError('Sign in to react');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'reaction',
        message_id: messageId,
        emoji
      }));
    }
    setActiveReactionPicker(null);
  };

  const hasUserReacted = (reactions, emoji) => {
    return reactions?.[emoji]?.includes(user?.user_id);
  };

  const getTotalReactions = (reactions) => {
    if (!reactions) return 0;
    return Object.values(reactions).reduce((sum, users) => sum + users.length, 0);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col h-[500px]">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5" />
          <span className="font-semibold">Live Chat</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          <span>{onlineCount} online</span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={msg.message_id || idx} className="group flex items-start gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                {msg.user_image ? (
                  <img src={msg.user_image} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  msg.user_name?.charAt(0)?.toUpperCase() || '?'
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-gray-900 text-sm">{msg.user_name}</span>
                  <span className="text-xs text-gray-400">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className="text-gray-700 text-sm break-words">{msg.content}</p>
                
                {/* Reactions display */}
                {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {Object.entries(msg.reactions).map(([emoji, users]) => (
                      <button
                        key={emoji}
                        onClick={(e) => { e.stopPropagation(); handleReaction(msg.message_id, emoji); }}
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs transition ${
                          hasUserReacted(msg.reactions, emoji)
                            ? 'bg-purple-100 text-purple-700 border border-purple-300'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`reaction-${emoji}-${msg.message_id}`}
                      >
                        <span>{emoji}</span>
                        <span className="font-medium">{users.length}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Reaction picker trigger */}
              <div className="relative opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setActiveReactionPicker(activeReactionPicker === msg.message_id ? null : msg.message_id);
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full text-gray-400 hover:text-gray-600"
                  data-testid={`reaction-trigger-${msg.message_id}`}
                >
                  <Smile className="h-4 w-4" />
                </button>
                
                {/* Emoji picker popup */}
                {activeReactionPicker === msg.message_id && (
                  <div 
                    className="absolute right-0 top-7 bg-white rounded-lg shadow-lg border p-2 flex gap-1 z-20"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {REACTION_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(msg.message_id, emoji)}
                        className={`w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-lg transition transform hover:scale-110 ${
                          hasUserReacted(msg.reactions, emoji) ? 'bg-purple-100' : ''
                        }`}
                        data-testid={`emoji-picker-${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t bg-white">
        {user ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              maxLength={500}
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || !connected}
              className="p-2 bg-purple-500 text-white rounded-full hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="chat-send-btn"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => navigate('/auth')}
            className="w-full py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600"
          >
            Sign in to chat
          </button>
        )}
      </div>
    </div>
  );
};

// ============ POST CARD ============
const PostCard = ({ post, currentUser, onDelete }) => {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const checkLiked = async () => {
      if (!currentUser) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/community/posts/${post.post_id}/liked`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setLiked(data.liked);
      } catch (e) {}
    };
    checkLiked();
  }, [post.post_id, currentUser]);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community/posts/${post.post_id}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLiked(data.action === 'liked');
      setLikeCount(data.like_count);
    } catch (e) {}
  };

  const handleShare = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/community/post/${post.post_id}`);
    alert('Link copied!');
  };

  const typeInfo = POST_TYPES.find(t => t.id === post.type) || POST_TYPES[1];

  return (
    <div 
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition cursor-pointer"
      onClick={() => navigate(`/community/post/${post.post_id}`)}
      data-testid={`post-card-${post.post_id}`}
    >
      <div className="p-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold">
            {post.user_image ? (
              <img src={post.user_image} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              post.user_name?.charAt(0)?.toUpperCase() || '?'
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">{post.user_name}</p>
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <span>{formatRelativeTime(post.created_at)}</span>
              {post.location && (
                <span className="flex items-center gap-0.5">
                  <MapPin className="h-3 w-3" />
                  {post.location}
                </span>
              )}
              <span className="px-2 py-0.5 bg-gray-100 rounded-full">{typeInfo.icon} {typeInfo.label}</span>
            </div>
          </div>
        </div>
        <div className="relative">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <MoreHorizontal className="h-5 w-5 text-gray-400" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-8 bg-white shadow-lg rounded-lg border py-1 z-10 min-w-[140px]">
              <button 
                onClick={(e) => { e.stopPropagation(); setShowMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
              >
                <Flag className="h-4 w-4" /> Report
              </button>
              {currentUser?.user_id === post.user_id && (
                <button 
                  onClick={(e) => { e.stopPropagation(); onDelete?.(post.post_id); setShowMenu(false); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pb-3">
        <p className="text-gray-800 whitespace-pre-wrap line-clamp-4">{post.text_body}</p>
      </div>

      {post.media_urls?.length > 0 && (
        <div className={`grid gap-1 ${post.media_urls.length === 1 ? '' : post.media_urls.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {post.media_urls.slice(0, 4).map((url, idx) => (
            <div key={idx} className="aspect-square relative">
              <img src={url} alt="" className="w-full h-full object-cover" />
              {idx === 3 && post.media_urls.length > 4 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white text-xl font-bold">
                  +{post.media_urls.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {post.video_url && (
        <div className="aspect-video bg-black">
          <video src={post.video_url} controls className="w-full h-full" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {post.attached_listing && (
        <div 
          className="mx-4 mb-3 p-3 bg-gray-50 rounded-lg flex items-center gap-3 hover:bg-gray-100"
          onClick={(e) => { e.stopPropagation(); navigate(`/item/${post.attached_listing.listing_id}`); }}
        >
          {post.attached_listing.images?.[0] && (
            <img src={post.attached_listing.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{post.attached_listing.title}</p>
            <p className="text-emerald-600 font-semibold">${post.attached_listing.price?.toLocaleString()}</p>
          </div>
          <Link2 className="h-5 w-5 text-gray-400" />
        </div>
      )}

      <div className="px-4 py-3 border-t flex items-center gap-6">
        <button 
          onClick={handleLike}
          className={`flex items-center gap-2 text-sm ${liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
        >
          <Heart className={`h-5 w-5 ${liked ? 'fill-current' : ''}`} />
          <span>{likeCount}</span>
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); navigate(`/community/post/${post.post_id}`); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-600"
        >
          <MessageSquare className="h-5 w-5" />
          <span>{post.comment_count || 0}</span>
        </button>
        <button 
          onClick={handleShare}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-500"
        >
          <Share2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// ============ MAIN COMMUNITY PAGE ============
const CommunityPage = () => {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeType, setActiveType] = useState(searchParams.get('type') || 'all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'feed');
  const [userListings, setUserListings] = useState([]);

  // Fetch user's listings for attachment
  useEffect(() => {
    const fetchListings = async () => {
      if (!user) return;
      try {
        const res = await api.get(`/listings?seller_id=${user.user_id}&status=active&limit=50`);
        setUserListings(res.data.listings || []);
      } catch (e) {}
    };
    fetchListings();
  }, [user]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeType !== 'all') params.set('type', activeType);
      if (searchQuery) params.set('search', searchQuery);
      params.set('page', page.toString());
      params.set('limit', '20');

      const res = await fetch(`${API_URL}/api/community/posts?${params}`);
      const data = await res.json();
      
      setPosts(data.posts || []);
      setTotalPages(data.pages || 1);
    } catch (e) {
      console.error('Error fetching posts:', e);
    }
    setLoading(false);
  }, [activeType, searchQuery, page]);

  useEffect(() => {
    if (activeTab === 'feed') {
      fetchPosts();
    }
  }, [fetchPosts, activeTab]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeType !== 'all') params.set('type', activeType);
    if (searchQuery) params.set('search', searchQuery);
    if (activeTab !== 'feed') params.set('tab', activeTab);
    setSearchParams(params);
  }, [activeType, searchQuery, activeTab, setSearchParams]);

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/community/posts/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.filter(p => p.post_id !== postId));
    } catch (e) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header user={user} />
      
      {/* Spacer for fixed header */}
      <div className="h-[184px]"></div>
      
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-2">BidZuu Community</h1>
          <p className="text-emerald-100">Connect, share deals, ask questions, and chat live!</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-16">
        {/* Main Tabs: Feed / Chat / Groups */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition ${
              activeTab === 'feed'
                ? 'bg-emerald-500 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border'
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            Community Feed
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition ${
              activeTab === 'chat'
                ? 'bg-purple-500 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border'
            }`}
          >
            <Radio className="h-4 w-4" />
            Live Chat
          </button>
          <button
            onClick={() => navigate('/community/groups')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition bg-white text-gray-700 hover:bg-gray-50 border"
          >
            <Users className="h-4 w-4" />
            Groups
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'feed' ? (
              <>
                {/* Enhanced Post Composer */}
                <PostComposer 
                  user={user} 
                  userListings={userListings}
                  onPost={(newPost) => setPosts(prev => [newPost, ...prev])}
                />

                {/* Search */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search posts..."
                        className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        data-testid="community-search"
                      />
                    </div>
                  </div>
                </div>

                {/* Type Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide">
                  {POST_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => { setActiveType(type.id); setPage(1); }}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                        activeType === type.id
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border'
                      }`}
                    >
                      {type.icon} {type.label}
                    </button>
                  ))}
                </div>

                {/* Posts */}
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl">
                    <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No posts yet. Be the first to share!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {posts.map((post) => (
                      <PostCard 
                        key={post.post_id} 
                        post={post} 
                        currentUser={user}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 bg-white"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 bg-white rounded-lg border">Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 bg-white"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <ChatRoom user={user} />
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* Live Chat Preview (when on feed) */}
            {activeTab === 'feed' && (
              <div 
                onClick={() => setActiveTab('chat')}
                className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-4 text-white cursor-pointer hover:shadow-lg transition"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Radio className="h-5 w-5" />
                  <span className="font-semibold">Live Chat Room</span>
                </div>
                <p className="text-purple-200 text-sm">Join the conversation with other community members in real-time!</p>
                <button className="mt-3 px-4 py-2 bg-white/20 rounded-lg text-sm font-medium hover:bg-white/30 transition">
                  Join Chat →
                </button>
              </div>
            )}

            {/* Community Guidelines */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Community Guidelines</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5" />
                  Be respectful and kind
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5" />
                  No spam, scams, or misleading content
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5" />
                  Share genuine deals and questions
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5" />
                  Report inappropriate content
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <a href="/privacy" className="block text-sm text-gray-600 hover:text-emerald-600">Privacy Policy</a>
                <a href="/terms" className="block text-sm text-gray-600 hover:text-emerald-600">Terms of Service</a>
                <a href="/help" className="block text-sm text-gray-600 hover:text-emerald-600">Help Center</a>
                <a href="/contact" className="block text-sm text-gray-600 hover:text-emerald-600">Contact Us</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
